import { useState } from "react";
import type { FormEvent } from "react";
import { useApi } from "@/hooks/useApi";
import { StatusBadge } from "@/components/StatusBadge";
import type { FormUploadProgress } from "@/api/client";
import {
  formatPluginList,
  normalizePlugin,
  pluginRuntimeLabel,
  pluginSourceLabel,
  pluginTrustBadge,
} from "@/lib/plugins";
import {
  getPlugins,
  getPluginCatalog,
  installPlugin,
  installPluginFromCatalog,
  removePlugin,
  enablePlugin,
  disablePlugin,
} from "@/api/endpoints";

export function PluginsPage() {
  const plugins = useApi(() => getPlugins(), []);
  const catalog = useApi(() => getPluginCatalog(), []);
  const [pendingPluginId, setPendingPluginId] = useState<string | null>(null);
  const [pendingCatalogId, setPendingCatalogId] = useState<string | null>(null);
  const [installing, setInstalling] = useState(false);
  const [installProgress, setInstallProgress] = useState<FormUploadProgress | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);

  async function handleInstall(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const input = form.elements.namedItem("plugin-package");
    if (!(input instanceof HTMLInputElement) || !input.files?.[0]) {
      setToggleError("Choose a plugin zip file");
      return;
    }
    setInstalling(true);
    setInstallProgress({
      loaded: 0,
      total: input.files[0].size > 0 ? input.files[0].size : null,
      percent: input.files[0].size > 0 ? 0 : null,
      phase: "upload",
    });
    setToggleError(null);
    try {
      await installPlugin(input.files[0], setInstallProgress);
      form.reset();
      plugins.refetch();
    } catch (error) {
      setToggleError(error instanceof Error ? error.message : "Failed to install plugin");
    } finally {
      setInstalling(false);
      setInstallProgress(null);
    }
  }

  async function handleCatalogInstall(id: string) {
    setPendingCatalogId(id);
    setToggleError(null);
    try {
      await installPluginFromCatalog(id);
      catalog.refetch();
      plugins.refetch();
    } catch (error) {
      setToggleError(error instanceof Error ? error.message : "Failed to install plugin");
    } finally {
      setPendingCatalogId(null);
    }
  }

  async function handleToggle(pluginId: string, enabled: boolean) {
    setPendingPluginId(pluginId);
    setToggleError(null);
    try {
      if (enabled) {
        await disablePlugin(pluginId);
      } else {
        await enablePlugin(pluginId);
      }
      plugins.refetch();
    } catch (error) {
      setToggleError(error instanceof Error ? error.message : "Failed to update plugin");
    } finally {
      setPendingPluginId(null);
    }
  }

  async function handleRemove(pluginId: string) {
    if (!window.confirm(`Remove plugin ${pluginId}?`)) {
      return;
    }
    setPendingPluginId(pluginId);
    setToggleError(null);
    try {
      await removePlugin(pluginId);
      plugins.refetch();
    } catch (error) {
      setToggleError(error instanceof Error ? error.message : "Failed to remove plugin");
    } finally {
      setPendingPluginId(null);
    }
  }

  if (plugins.loading) return <p>Loading...</p>;
  if (plugins.error) return <p className="error-message">Failed to load plugins</p>;

  const installPercent = installProgress?.percent ?? (installProgress?.phase === "processing" ? 100 : null);
  const installButtonLabel = installing
    ? installProgress?.phase === "upload" && installPercent !== null
      ? `Uploading ${installPercent}%`
      : "Installing"
    : "Upload";

  return (
    <div className="admin-page">
      <h1>Plugins</h1>

      <section className="plugin-catalog">
        <h2 className="plugin-catalog-heading">Plugin Catalog</h2>
        {catalog.loading && <p>Loading catalog…</p>}
        {catalog.error && <p className="error-message">Failed to load catalog</p>}
        {!catalog.loading && !catalog.error && (catalog.data?.data.length ?? 0) === 0 && (
          <p className="jobs-subtext">
            No plugins in the catalog. Use “Install a custom plugin” below to upload a package.
          </p>
        )}
        <div className="plugin-catalog-list">
          {catalog.data?.data.map((entry) => (
            <div key={entry.id} className="plugin-catalog-card">
              <div>
                <strong>{entry.attributes.name}</strong>
                <div className="jobs-subtext">{entry.attributes.description}</div>
              </div>
              <button
                className={
                  entry.attributes.installed
                    ? "btn btn--secondary btn--compact"
                    : "btn btn--primary btn--compact"
                }
                disabled={pendingCatalogId === entry.id}
                onClick={() => handleCatalogInstall(entry.id)}
              >
                {pendingCatalogId === entry.id
                  ? entry.attributes.installed
                    ? "Updating…"
                    : "Installing…"
                  : entry.attributes.installed
                    ? "Update / Reinstall"
                    : "Install"}
              </button>
            </div>
          ))}
        </div>
      </section>

      <h2 className="plugins-installed-heading">Installed Plugins</h2>
      {toggleError && <p className="error-message">{toggleError}</p>}
      <table className="data-table plugins-table">
        <colgroup>
          <col className="plugins-col-plugin" />
          <col className="plugins-col-version" />
          <col className="plugins-col-state" />
          <col className="plugins-col-actions" />
        </colgroup>
        <thead>
          <tr>
            <th>Plugin</th>
            <th>Version</th>
            <th>State</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {plugins.data?.data.map((plugin) => {
            const normalized = normalizePlugin(plugin);
            if (!normalized.valid) {
              console.error("Invalid plugin payload:", plugin);
            }
            const contracts = normalized.contracts;
            const permissions = normalized.permissions;
            const isBuiltin = normalized.source === "builtin";
            const trust = pluginTrustBadge(normalized);
            return (
              <tr key={normalized.id}>
                <td>
                  <strong>{normalized.name}</strong>
                  {normalized.description && (
                    <div className="jobs-subtext">{normalized.description}</div>
                  )}
                  <div className="plugin-meta-line">
                    <span>{pluginSourceLabel(normalized)}</span>
                    <span>{pluginRuntimeLabel(normalized)}</span>
                    <span className={`plugin-signature-badge plugin-signature-badge--${trust.kind}`}>
                      {trust.label}
                    </span>
                  </div>
                  <details className="plugin-details">
                    <summary>Technical details</summary>
                    <dl className="plugin-details-list">
                      <dt>Plugin ID</dt>
                      <dd><code>{normalized.id}</code></dd>
                      <dt>Capabilities</dt>
                      <dd>{formatPluginList(contracts)}</dd>
                      <dt>Permissions</dt>
                      <dd>{formatPluginList(permissions)}</dd>
                      <dt>API schema</dt>
                      <dd>
                        <a href={normalized.openapiUrl} target="_blank" rel="noreferrer">
                          OpenAPI
                        </a>
                      </dd>
                    </dl>
                  </details>
                </td>
                <td>
                  <span className="plugin-version-badge">v{normalized.version}</span>
                </td>
                <td>
                  <StatusBadge status={normalized.status} />
                  <div className="jobs-subtext">
                    {normalized.enabled ? "enabled" : "disabled"}
                  </div>
                </td>
                <td>
                  <div className="plugins-actions">
                    <button
                      onClick={() => handleToggle(normalized.id, normalized.enabled)}
                      className="action-btn"
                      disabled={isBuiltin || !normalized.valid || pendingPluginId === normalized.id}
                    >
                      {pendingPluginId === normalized.id
                        ? "Saving"
                        : normalized.enabled
                          ? "Disable"
                          : "Enable"}
                    </button>
                    <button
                      onClick={() => handleRemove(normalized.id)}
                      className="action-btn action-btn--danger"
                      disabled={isBuiltin || !normalized.valid || pendingPluginId === normalized.id}
                    >
                      Remove
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <details className="plugins-upload-advanced">
        <summary>Install a custom plugin</summary>
        <p className="jobs-subtext">Upload a plugin package (.zip) that isn’t in the catalog.</p>
        <form className="plugins-install-form" onSubmit={handleInstall}>
          <input id="plugin-package" name="plugin-package" type="file" accept=".zip,application/zip" />
          <button className="btn btn--secondary btn--compact" type="submit" disabled={installing}>
            {installButtonLabel}
          </button>
        </form>
        {installing && installProgress && (
          <div className="plugins-install-progress" role="status" aria-live="polite">
            <progress aria-label="Plugin upload progress" max={100} value={installPercent ?? undefined} />
          </div>
        )}
      </details>
    </div>
  );
}
